import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../application/provider";
import { Button, Form, FormGroup, Input, Label, Modal, ModalBody, ModalHeader, Table } from "reactstrap";
import ProveedoresService from "../axios_services/proveedores.service";
import ProveedorServiciosService from "../axios_services/proveedorservicios.service";
import Loader from "../components/Loader/Loader";
import { ButtonDelete, ButtonInsert, ButtonSearch, ButtonUpdate } from "../components/Buttons/Buttons";
import ModalConfirmar from "../components/Modal/ModalConfirmar";
import "./PagoProveedores.css";

const FORM_INICIAL = {
    descripcion_servicio: "",
    proveedor_id: "",
    precio: "",
    incluye_igv: false,
    frecuencia_pago: "mensual",
};

function GestionServiciosProveedor() {
    const [state] = useContext(AppContext);
    const [proveedores, setProveedores] = useState([]);
    const [servicios, setServicios] = useState([]);
    const [loading, setLoading] = useState(false);
    const [proveedorFiltro, setProveedorFiltro] = useState("");
    const [modalFormulario, setModalFormulario] = useState(false);
    const [servicioSeleccionado, setServicioSeleccionado] = useState(null);
    const [form, setForm] = useState(FORM_INICIAL);
    const [mensajeModal, setMensajeModal] = useState({ isOpen: false, titulo: "", mensaje: "", variante: "exito" });
    const [confirmacionModal, setConfirmacionModal] = useState({ isOpen: false, titulo: "", mensaje: "", textConfirmar: "Confirmar", variante: "exito", onConfirmar: null });
    const initialFetchDone = useRef(false);
    const perfilId = Number(state.user?.perfil_id || state.perfil_id);
    const puedeRegistrar = [8, 11, 12, 21, 26].includes(perfilId);

    const formatoSoles = (monto) => new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" }).format(Number(monto || 0));

    const mostrarMensaje = useCallback((titulo, mensaje, variante = "exito") => {
        setMensajeModal({ isOpen: true, titulo, mensaje, variante });
    }, []);

    const obtenerMensajeError = (error, fallback) => {
        const errores = error.response?.data?.errors;
        if (errores) return Object.values(errores).flat().join("\n");
        return error.response?.data?.message || fallback;
    };

    const cargarProveedores = useCallback(async () => {
        const response = await ProveedoresService.getProveedores();
        setProveedores(response.data || []);
    }, []);

    const cargarServicios = useCallback(async () => {
        setLoading(true);
        try {
            const response = await ProveedorServiciosService.getServiciosProveedor(proveedorFiltro);
            setServicios(response.data || []);
        } catch (error) {
            mostrarMensaje("Error", obtenerMensajeError(error, "Error al cargar servicios"), "peligro");
            setServicios([]);
        } finally {
            setLoading(false);
        }
    }, [proveedorFiltro, mostrarMensaje]);

    useEffect(() => {
        if (!initialFetchDone.current) {
            cargarProveedores();
            cargarServicios();
            initialFetchDone.current = true;
        }
    }, [cargarProveedores, cargarServicios]);

    const abrirNuevo = () => {
        setServicioSeleccionado(null);
        setForm({ ...FORM_INICIAL, proveedor_id: proveedorFiltro || "" });
        setModalFormulario(true);
    };

    const abrirEditar = (servicio) => {
        setServicioSeleccionado(servicio);
        setForm({
            descripcion_servicio: servicio.descripcion_servicio || "",
            proveedor_id: servicio.proveedor_id || "",
            precio: servicio.precio || "",
            incluye_igv: Number(servicio.incluye_igv) === 1,
            frecuencia_pago: servicio.frecuencia_pago || "mensual",
        });
        setModalFormulario(true);
    };

    const cerrarFormulario = () => {
        setModalFormulario(false);
        setServicioSeleccionado(null);
        setForm(FORM_INICIAL);
    };

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setForm(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    };

    const guardarServicio = async () => {
        if (!form.descripcion_servicio || !form.proveedor_id || !form.precio) {
            mostrarMensaje("Validación", "Complete los campos obligatorios", "peligro");
            return;
        }

        setLoading(true);
        try {
            await ProveedorServiciosService.guardarServicioProveedor(form, servicioSeleccionado?.servicio_id);
            cerrarFormulario();
            await cargarServicios();
        } catch (error) {
            mostrarMensaje("Error", obtenerMensajeError(error, "Error al guardar el servicio"), "peligro");
        } finally {
            setLoading(false);
        }
    };

    const eliminarServicio = (servicio) => {
        setConfirmacionModal({
            isOpen: true,
            titulo: "Eliminar servicio",
            mensaje: "¿Está seguro de eliminar este servicio?",
            textConfirmar: "Eliminar",
            variante: "peligro",
            onConfirmar: async () => {
                setConfirmacionModal(prev => ({ ...prev, isOpen: false, onConfirmar: null }));
                setLoading(true);
                try {
                    await ProveedorServiciosService.eliminarServicioProveedor(servicio.servicio_id);
                    await cargarServicios();
                } catch (error) {
                    mostrarMensaje("Error", obtenerMensajeError(error, "Error al eliminar el servicio"), "peligro");
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    return (
        <div className="caja-flujo-container pago-proveedores-scope">
            {loading && <Loader />}

            <div className="flex items-center justify-between mb-4">
                <h1>GESTIÓN DE SERVICIOS</h1>
            </div>

            <div className="filtros">
                <div className="campo-fecha">
                    <Label>Proveedor:</Label>
                    <Input type="select" value={proveedorFiltro} onChange={(e) => setProveedorFiltro(e.target.value)}>
                        <option value="">Todos los proveedores</option>
                        {proveedores.map(proveedor => (
                            <option key={proveedor.proveedor_id} value={proveedor.proveedor_id}>{proveedor.nombre_razon_social}</option>
                        ))}
                    </Input>
                </div>
            </div>

            <ButtonSearch onClick={cargarServicios} isLoading={loading} />

            <div className="modulo-toolbar">
                {puedeRegistrar && <ButtonInsert onClick={abrirNuevo} />}
            </div>

            <div className="numero-propuestas">
                <p>N° servicios: {servicios.length}</p>
            </div>

            <div className="table-container">
                <Table>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>DESCRIPCIÓN DEL SERVICIO</th>
                            <th>PROVEEDOR</th>
                            <th>PRECIO</th>
                            <th>IGV</th>
                            <th>FRECUENCIA</th>
                            <th>ACCIONES</th>
                        </tr>
                    </thead>
                    <tbody>
                        {servicios.length === 0 ? (
                            <tr><td colSpan="7" className="sin-data">No hay servicios registrados</td></tr>
                        ) : servicios.map(servicio => (
                            <tr key={servicio.servicio_id}>
                                <td>{servicio.servicio_id}</td>
                                <td className="descripcion-col">{servicio.descripcion_servicio}</td>
                                <td>{servicio.nombre_razon_social}</td>
                                <td>{formatoSoles(servicio.precio)}</td>
                                <td>{Number(servicio.incluye_igv) === 1 ? "Sí" : "No"}</td>
                                <td>{servicio.frecuencia_pago}</td>
                                <td>
                                    <div className="acciones-container">
                                        {puedeRegistrar && <ButtonUpdate onClick={() => abrirEditar(servicio)} />}
                                        {puedeRegistrar && <ButtonDelete onClick={() => eliminarServicio(servicio)} />}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            <Modal isOpen={modalFormulario} className="custom-modal" backdrop="static" size="lg" fade={false}>
                <ModalHeader className="modal-header">
                    <div className="titulo">
                        <h1>{servicioSeleccionado ? "EDITAR SERVICIO" : "REGISTRAR SERVICIO"}</h1>
                        <Button className="close-btn" onClick={cerrarFormulario}>X</Button>
                    </div>
                </ModalHeader>
                <div className="modal-body-footer">
                    <ModalBody className="modal-body">
                        <Form className="fila" onSubmit={(e) => e.preventDefault()}>
                            <FormGroup className="full-width">
                                <Label>Descripción del servicio:</Label>
                                <Input className="form-control" type="textarea" name="descripcion_servicio" value={form.descripcion_servicio} onChange={handleChange} />
                            </FormGroup>
                            <FormGroup>
                                <Label>Proveedor:</Label>
                                <Input className="form-control" type="select" name="proveedor_id" value={form.proveedor_id} onChange={handleChange}>
                                    <option value="">Seleccione</option>
                                    {proveedores.map(proveedor => (
                                        <option key={proveedor.proveedor_id} value={proveedor.proveedor_id}>{proveedor.nombre_razon_social}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                            <FormGroup>
                                <Label>Precio:</Label>
                                <div className="input-group custom-input-group">
                                    <span className="input-group-text custom-symbol">S/.</span>
                                    <Input className="form-control" type="number" step="0.01" name="precio" value={form.precio} onChange={handleChange} />
                                </div>
                            </FormGroup>
                            <FormGroup>
                                <Label>Frecuencia:</Label>
                                <Input className="form-control" type="select" name="frecuencia_pago" value={form.frecuencia_pago} onChange={handleChange}>
                                    <option value="mensual">Mensual</option>
                                    <option value="semestral">Semestral</option>
                                    <option value="anual">Anual</option>
                                </Input>
                            </FormGroup>
                            <FormGroup>
                                <Label>Incluye IGV:</Label>
                                <div>
                                    <Input type="checkbox" name="incluye_igv" checked={form.incluye_igv} onChange={handleChange} />
                                </div>
                            </FormGroup>
                            <FormGroup>
                                <div className="button-container">
                                    <Button className="button-custom" onClick={() => setConfirmacionModal({
                                        isOpen: true,
                                        titulo: "Confirmar registro",
                                        mensaje: servicioSeleccionado ? "¿Está seguro de actualizar este servicio?" : "¿Está seguro de guardar este servicio?",
                                        textConfirmar: "Guardar",
                                        variante: "exito",
                                        onConfirmar: async () => {
                                            setConfirmacionModal(prev => ({ ...prev, isOpen: false, onConfirmar: null }));
                                            await guardarServicio();
                                        },
                                    })}>
                                        GUARDAR
                                    </Button>
                                </div>
                            </FormGroup>
                        </Form>
                    </ModalBody>
                </div>
            </Modal>

            <ModalConfirmar
                isOpen={mensajeModal.isOpen}
                titulo={mensajeModal.titulo}
                mensaje={mensajeModal.mensaje}
                textConfirmar="Aceptar"
                textCancelar="Cerrar"
                variante={mensajeModal.variante}
                onConfirmar={() => setMensajeModal(prev => ({ ...prev, isOpen: false }))}
                onCancelar={() => setMensajeModal(prev => ({ ...prev, isOpen: false }))}
            />
            <ModalConfirmar
                isOpen={confirmacionModal.isOpen}
                titulo={confirmacionModal.titulo}
                mensaje={confirmacionModal.mensaje}
                textConfirmar={confirmacionModal.textConfirmar}
                textCancelar="Cancelar"
                variante={confirmacionModal.variante}
                onConfirmar={() => confirmacionModal.onConfirmar && confirmacionModal.onConfirmar()}
                onCancelar={() => setConfirmacionModal(prev => ({ ...prev, isOpen: false, onConfirmar: null }))}
            />
        </div>
    );
}

export default GestionServiciosProveedor;
